import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { withSidebar } from "../hoc/withSidebar";
import warningIcon from "../../assets/images/warning-icon.png";
import CommonLayout from "../custom/containers/CommonLayout";
import {getBillerCrossReferenceGridHeadings, EMPTY_BILLER_CROSS_REF_CRITERIA, MANAGE_BILLER_CROSS_REF_PROFILE_BASE,}
  from "../../constants/ManageBillerCrossReferenceConstants";
import { getComponentAccess } from "../../utils/helper";
import actions from "../../actions";

const DEFAULT_PAGE_SIZE = 25;

/** BMO / BNS / CIBC biller id search values must be digits only when non-empty. */
const FI_BILLER_ID_NUMERIC = /^\d+$/;

const ManageBillerCrossReference = (props) => {
  const { t } = useTranslation();
  const { history, location: { state } = {} } = props;
  const loginUser = useSelector((state) => state.loginUserReducer?.user);
  const componentAccessData =
    state?.componentAccess ||
    getComponentAccess({ function_type_code: "MBCR", label_type_code: "BM" }, loginUser);

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(EMPTY_BILLER_CROSS_REF_CRITERIA);
  const searchCriteriaRef = useRef(searchCriteria);

  useEffect(() => {
    searchCriteriaRef.current = searchCriteria;
  }, [searchCriteria]);

  const [errorMssg, setErrorMsg] = useState(t("erps.billermanagement.crossreference.error.emptyinput"));
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSearchValid, setIsSearchValid] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [bcrListData, setBcrListData] = useState(null);
  const [dataCount, setDataCount] = useState(-1);

  const bcrResponse = useSelector((state) => state.billerCrossReferenceReducer);

  useEffect(() => {
    if (state?.fromDetailPage && bcrResponse?.bcrDetails) {
      const next = { ...EMPTY_BILLER_CROSS_REF_CRITERIA };
      Object.keys(next).forEach((key) => {
        if (bcrResponse.bcrDetails[key] != null && bcrResponse.bcrDetails[key] !== "") {
          next[key] = bcrResponse.bcrDetails[key];
        }
      });
      setSearchCriteria(next);
      if (bcrResponse?.bcrData != null) {
        const bcr = bcrResponse?.bcrData;
        setBcrListData(bcr?.data);
        setDataCount( bcr?.count || 0);
        const limit = bcrResponse.bcrDetails?.limit || DEFAULT_PAGE_SIZE;
        const offset = bcrResponse.bcrDetails?.offset;
        setRowPerPage(limit);
        setPageNumber(limit ? (offset / limit) : 0);
      }
    } else {
      dispatch({
        type: "GET_BILLER_CROSS_REFERENCE_SUCCESS",
        data: { data: null, bcrDetails: null },
      });
    }
  }, []);

  useEffect(() => {
    if (!bcrResponse?.bcrData) {
      setBcrListData(null);
      setDataCount(-1);
      return;
    }
    const bcr = bcrResponse?.bcrData;
    setBcrListData(bcr?.data);
    setDataCount(bcr?.count || 0);
    if (bcrResponse?.bcrDetails?.limit != null) {
      setRowPerPage(bcrResponse.bcrDetails.limit);
    }
    if (bcrResponse?.bcrDetails?.offset != null && bcrResponse.bcrDetails.limit) {
      const limit = bcrResponse.bcrDetails.limit;
      setPageNumber(bcrResponse.bcrDetails.offset / limit);
    }
  }, [bcrResponse]);

  const pathname = props.location?.pathname || "";
  const pageName = pathname.split("/")[1] || "";

  const searchCriteriaChangeHandler = useCallback((field) => (event) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  }, []);

  const hasAtLeastOneCriterion = () =>
    Object.values(searchCriteria).some(
      (v) => typeof v === "string" && v.trim().length > 0
    );

  const getEmptyHighlight = () =>
    Object.keys(EMPTY_BILLER_CROSS_REF_CRITERIA).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );

  const handleSearch = () => {
    let errors = [];
    let highlightErrorV1 = {};

    if (!hasAtLeastOneCriterion()) {
      errors.push(t("erps.billermanagement.crossreference.error.emptyinput"));
      highlightErrorV1 = { ...getEmptyHighlight() };
    } else {
      const { bmoBillerId, bnsBillerId, cibcBillerId } = searchCriteria;
      if (
        typeof bmoBillerId === "string" &&
        bmoBillerId.trim() !== "" &&
        !FI_BILLER_ID_NUMERIC.test(bmoBillerId.trim())
      ) {
        errors.push(t("erps.billermanagement.crossreference.error.bmoBillerIdNumeric"));
        highlightErrorV1 = { ...highlightErrorV1, bmoBillerId: true };
      }
      if (
        typeof bnsBillerId === "string" &&
        bnsBillerId.trim() !== "" &&
        !FI_BILLER_ID_NUMERIC.test(bnsBillerId.trim())
      ) {
        errors.push(t("erps.billermanagement.crossreference.error.bnsBillerIdNumeric"));
        highlightErrorV1 = { ...highlightErrorV1, bnsBillerId: true };
      }
      if (
        typeof cibcBillerId === "string" &&
        cibcBillerId.trim() !== "" &&
        !FI_BILLER_ID_NUMERIC.test(cibcBillerId.trim())
      ) {
        errors.push(t("erps.billermanagement.crossreference.error.cibcBillerIdNumeric"));
        highlightErrorV1 = { ...highlightErrorV1, cibcBillerId: true };
      }
    }

    if (errors && errors.length) {
      setIsSearchValid(false);
      setBcrListData(null);
      setDataCount(-1);
      setErrorMsg(errors);
      setFieldErrors(highlightErrorV1);
      return;
    }
    setIsSearchValid(true);
    setFieldErrors({});
    setPageNumber(0);
    loadResults({
      ...searchCriteria,
      offset: 0,
      limit: rowPerPage,
    });
  };

  const loadResults = (criteria) => {
    setErrorMsg(null);
    setLoading(true);
    dispatch(
        actions.billerCrossReferenceActions.searchBillerCrossReference(criteria, {
          history,
          setLoading,
          setErrorMessage: setErrorMsg,
        })
    );
  };

  const paginate = (queryParams) => {
    const startOffset = queryParams.startOffset;
    const limit = queryParams.endOffset - queryParams.startOffset;
    setPageNumber(startOffset / limit);
    setRowPerPage(limit);
    loadResults({
      ...searchCriteriaRef.current,
      offset: startOffset,
      limit,
    });
  };

  const resetHandler = () => {
    setIsSearchValid(true);
    setSearchCriteria(EMPTY_BILLER_CROSS_REF_CRITERIA);
    setBcrListData(null);
    setDataCount(-1);
    setPageNumber(0);
    setRowPerPage(DEFAULT_PAGE_SIZE);
    setFieldErrors({});
    setErrorMsg(t("erps.billermanagement.crossreference.error.emptyinput"));
    dispatch({
      type: "GET_BILLER_CROSS_REFERENCE_SUCCESS",
      data: { data: null, bcrDetails: null },
    });
  };

  const navigateToNew = () => {
    history.push(`${pathname}/new-biller-cross-reference`);
  };

  const gridColumns = getBillerCrossReferenceGridHeadings({
    listComponentAccess: componentAccessData,
    profilePathBase: MANAGE_BILLER_CROSS_REF_PROFILE_BASE,
  });

  const rowsForGrid = bcrListData != null ? bcrListData.map((row) => ({
          ...row, id: row.id})) : [];

  const searchResults = bcrListData !== null
      ? {
          rows: rowsForGrid,
          count: dataCount,
          columns: gridColumns,
        } : null;

  return (
    <CommonLayout
      pageTitle={t("erps.billermanagement.crossreference.page.title")}
      isRequired={false}
      pageDescription={t(
        "erps.billermanagement.crossreference.page.description"
      )}
      primaryButtonLabel={t(
        "erps.billermanagement.crossreference.button.newCrossReference"
      )}
      pageSubHeading={t("erps.billermanagement.crossreference.search.heading")}
      pageSubHeadingDescription={t(
        "erps.billermanagement.crossreference.search.subheading"
      )}
      errorMssg={errorMssg}
      primaryButtonLabelV2={t("erps.common.button.reset")}
      secondaryButtonLabel={t("erps.common.button.filter")}
      searchResultLabel={t(
        "erps.billermanagement.crossreference.search.results"
      )}
      searchResultFoundLabel={t(
        "erps.billermanagement.crossreference.search.recordsfound"
      )}
      searchNotStarted={t(
        "erps.billermanagement.crossreference.search.notstarted"
      )}
      firstTextInputFieldLabel={t(
        "erps.billermanagement.crossreference.label.centralizedBillerId"
      )}
      firstTextInputFieldId="centralizedBillerId"
      secondTextInputFieldLabel={t(
        "erps.billermanagement.crossreference.label.bmoBillerId"
      )}
      secondTextInputFieldId="bmoBillerId"
      thirdTextInputFieldId="bnsBillerId"
      thirdTextInputFieldLabel={t(
        "erps.billermanagement.crossreference.label.bnsBillerId"
      )}
      fourthTextInputFieldId="cibcBillerId"
      fourthTextInputFieldLabel={t(
        "erps.billermanagement.crossreference.label.cibcBillerId"
      )}
      startSearchHandler={handleSearch}
      searchCriteriaChangeHandler={searchCriteriaChangeHandler}
      resetHandler={resetHandler}
      navigateToNewUserComponent={navigateToNew}
      isSearchValid={isSearchValid}
      warningIcon={warningIcon}
      loading={loading}
      searchCriteria={searchCriteria}
      initiateSearch={paginate}
      pageNumber={pageNumber}
      rowPerPage={rowPerPage}
      searchResults={searchResults}
      pageName={pageName}
      pathName="/manage-biller-cross-reference"
      history={history}
      componentAccess={componentAccessData}
      actionType={{
        primaryButtonActionTypeV1: "hasNewBillerCrossReferenceAccess",
        firstTextInputFieldActionType: "hasCentralizedBillerIdTextField",
        secondTextInputFieldActionType: "hasBmoBillerIdTextField",
        thirdTextInputFieldActionType: "hasBnsBillerIdTextField",
        fourthTextInputFieldActionType: "hasCibcBillerIdTextField",
        primaryButtonActionTypeV2: "hasResetBillerCrossReferenceBtn",
        secondaryButtonActionType: "hasSearchBillerCrossReferenceAccess",
      }}
      isDisabled={true}
      dataGridWidth="dataGrid__box__container__manageBillerSearch"
      highlightError={fieldErrors}
      sumOfColumnWidth="true"
      initialStates={{
        sorting: {
          sortModel: [{ field: "centralizedBillerId", sort: "asc" }],
        },
      }}
    />
  );
};

export default withSidebar(ManageBillerCrossReference);
